import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Service } from '../../types';
import { servicesAPI } from '../../services/api';
import LoadingSpinner from '../common/LoadingSpinner';

interface Step2CompleteProps {
  onNext: (data: any) => void;
  onBack: () => void;
  initialData?: any;
}

const MAX_PRESCRIPTION_FILES = 5;

interface FormData {
  national_id: string;
  prescription_file?: File;
  prescription_tracking_code?: string;
}

const Step2Complete: React.FC<Step2CompleteProps> = ({ onNext, onBack, initialData }) => {
  const { register, handleSubmit, formState: { errors } } = useForm<FormData>({
    defaultValues: initialData
  });

  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [serviceSelectionError, setServiceSelectionError] = useState<string | null>(null);
  const [uploadMethod, setUploadMethod] = useState<'file' | 'code'>('file');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [fileListError, setFileListError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedServiceIds, setSelectedServiceIds] = useState<number[]>(() => {
    const ids = initialData?.service_ids;
    if (Array.isArray(ids) && ids.length > 0) {
      return [...ids];
    }
    if (initialData?.service_id) {
      return [initialData.service_id];
    }
    return [];
  });
  const fileInputId = 'prescription-file-input';

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await servicesAPI.getAll();
        setServices(response.data);
      } catch (err) {
        setError('خطا در بارگذاری خدمات');
        console.error('Error fetching services:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, []);

  const primaryButtonClass = 'inline-flex items-center justify-center rounded-lg bg-gray-900 text-white py-3 px-4 text-sm font-medium transition-colors hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-300 disabled:opacity-70 disabled:cursor-not-allowed';
  const secondaryButtonClass = 'inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-800 py-3 px-4 text-sm font-medium transition-colors hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-300';

  const toggleService = (id: number) => {
    setServiceSelectionError(null);
    setSelectedServiceIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const onSubmit = async (data: FormData) => {
    if (selectedServiceIds.length === 0) {
      setServiceSelectionError('حداقل یک خدمت را انتخاب کنید.');
      return;
    }
    if (uploadMethod === 'file' && selectedFiles.length === 0) {
      setFileListError('حداقل یک تصویر یا فایل PDF نسخه را اضافه کنید.');
      return;
    }
    setFileListError(null);
    setIsSubmitting(true);
    try {
      const formData = {
        ...data,
        service_ids: selectedServiceIds,
        service_id: selectedServiceIds[0],
        prescription_files: uploadMethod === 'file' ? selectedFiles : undefined,
        prescription_file: uploadMethod === 'file' && selectedFiles.length > 0 ? selectedFiles[0] : undefined,
      };
      onNext(formData);
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const validateNationalId = (value: string) => {
    if (!value) return 'کد ملی الزامی است';
    if (value.length !== 10) return 'کد ملی باید 10 رقم باشد';
    if (!/^\d{10}$/.test(value)) return 'کد ملی باید فقط شامل اعداد باشد';

    // الگوریتم بررسی کد ملی
    const digits = value.split('').map(Number);
    const checkDigit = digits[9];
    const sum = digits.slice(0, 9).reduce((acc, digit, index) => acc + digit * (10 - index), 0);
    const remainder = sum % 11;
    const calculatedCheckDigit = remainder < 2 ? remainder : 11 - remainder;

    if (calculatedCheckDigit !== checkDigit) {
      return 'کد ملی نامعتبر است';
    }

    return true;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      alert('فقط فایل‌های تصویری (JPG, PNG) و PDF مجاز است');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      alert('حجم فایل نباید بیشتر از 10 مگابایت باشد');
      return;
    }

    setFileListError(null);
    setSelectedFiles((prev) => {
      if (prev.length >= MAX_PRESCRIPTION_FILES) {
        alert(`حداکثر ${MAX_PRESCRIPTION_FILES} فایل می‌توانید اضافه کنید.`);
        return prev;
      }
      return [...prev, file];
    });
  };

  const removeFileAt = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  if (loading) {
    return <LoadingSpinner text="در حال بارگذاری خدمات..." />;
  }

  if (error) {
    return (
      <div className="max-w-md mx-auto">
        <div className="bg-white shadow-sm rounded-2xl border border-gray-200 p-6 text-center space-y-4">
          <p className="text-sm text-red-600">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className={secondaryButtonClass}
          >
            تلاش مجدد
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-lg mx-auto">
      <div className="bg-white rounded-2xl p-0 sm:p-6 shadow-none sm:shadow-sm sm:border sm:border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900 mb-6 text-center">تکمیل نوبت</h2>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">کد ملی</label>
            <input
              type="text"
              {...register('national_id', {
                required: 'کد ملی الزامی است',
                validate: validateNationalId
              })}
              className="w-full rounded-lg border border-gray-300 px-3 py-3 sm:py-2 text-base sm:text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-gray-400"
              placeholder="1234567890"
              maxLength={10}
              inputMode="numeric"
              dir="ltr"
            />
            {errors.national_id && (
              <p className="text-xs text-red-500">{errors.national_id.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">انتخاب خدمت (می‌توانید چند مورد را بزنید)</label>
            <div className="rounded-xl border border-gray-200 bg-gray-50/80 p-3 max-h-56 overflow-y-auto space-y-2">
              {services.filter((s) => s.is_active).map((service) => {
                const checked = selectedServiceIds.includes(service.id);
                return (
                  <label
                    key={service.id}
                    className={`flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-2.5 text-sm transition-colors ${
                      checked ? 'border-gray-900 bg-white shadow-sm' : 'border-transparent bg-white/60 hover:bg-white'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleService(service.id)}
                      className="mt-0.5 h-4 w-4 rounded border-gray-300 text-gray-900 focus:ring-gray-400"
                    />
                    <span className="flex-1 text-right text-gray-900 leading-snug">{service.name}</span>
                  </label>
                );
              })}
            </div>
            {serviceSelectionError && (
              <p className="text-xs text-red-500">{serviceSelectionError}</p>
            )}
          </div>

          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">نسخه پزشکی</label>

            <div className="flex gap-2" role="tablist">
              <button
                type="button"
                className={`flex-1 rounded-lg border text-sm font-medium py-3 sm:py-2.5 transition-colors ${uploadMethod === 'file' ? 'bg-gray-900 text-white border-gray-900' : 'bg-gray-100 text-gray-600 border-gray-200 hover:bg-gray-200'}`}
                onClick={() => {
                  setUploadMethod('file');
                  setFileListError(null);
                }}
                role="tab"
                aria-selected={uploadMethod === 'file'}
              >
                آپلود فایل
              </button>
              <button
                type="button"
                className={`flex-1 rounded-lg border text-sm font-medium py-3 sm:py-2.5 transition-colors ${uploadMethod === 'code' ? 'bg-gray-900 text-white border-gray-900' : 'bg-gray-100 text-gray-600 border-gray-200 hover:bg-gray-200'}`}
                onClick={() => {
                  setUploadMethod('code');
                  setSelectedFiles([]);
                  setFileListError(null);
                }}
                role="tab"
                aria-selected={uploadMethod === 'code'}
              >
                کد رهگیری
              </button>
            </div>

            {uploadMethod === 'file' && (
              <div className="space-y-3">
                <div className="upload-zone">
                  <input
                    id={fileInputId}
                    type="file"
                    accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
                    onChange={handleFileChange}
                    className="upload-zone__input"
                  />
                  <label htmlFor={fileInputId} className="upload-zone__label">
                    <span className="upload-zone__title">افزودن یک فایل (عکس یا PDF)</span>
                    <span className="upload-zone__hint">هر بار یک فایل انتخاب کنید؛ می‌توانید چند فایل به لیست اضافه کنید</span>
                  </label>
                </div>
                {selectedFiles.length > 0 && (
                  <ul className="rounded-lg border border-gray-200 bg-gray-50 divide-y divide-gray-200 text-sm text-gray-800">
                    {selectedFiles.map((f, idx) => (
                      <li key={`${f.name}-${idx}-${f.size}`} className="flex items-center justify-between gap-2 px-3 py-2">
                        <span className="truncate min-w-0" title={f.name}>{f.name}</span>
                        <button
                          type="button"
                          onClick={() => removeFileAt(idx)}
                          className="shrink-0 text-xs font-medium text-red-600 hover:text-red-800"
                        >
                          حذف
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                {fileListError && (
                  <p className="text-xs text-red-500">{fileListError}</p>
                )}
                <p className="text-xs text-gray-500">
                  فرمت‌های مجاز: JPG, PNG, PDF — هر فایل حداکثر 10MB — حداکثر {MAX_PRESCRIPTION_FILES} فایل
                </p>
              </div>
            )}

            {uploadMethod === 'code' && (
              <div className="space-y-2">
                <input
                  type="text"
                  {...register('prescription_tracking_code', {
                    required: uploadMethod === 'code' ? 'کد رهگیری الزامی است' : false
                  })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-3 sm:py-2 text-base sm:text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-gray-400"
                  placeholder="کد رهگیری"
                />
                {errors.prescription_tracking_code && (
                  <p className="text-xs text-red-500">{errors.prescription_tracking_code.message}</p>
                )}
              </div>
            )}
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onBack}
              className={`${secondaryButtonClass} flex-1 py-3.5 sm:py-3`}
            >
              مرحله قبل
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className={`${primaryButtonClass} flex-1 py-3.5 sm:py-3`}
            >
              {isSubmitting ? 'در حال ثبت...' : 'تکمیل نوبت'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Step2Complete;
